import { Box, Button, Container } from '@mui/material'
import { useOperationsStore } from '@operations/AppProvider'
import { FilterChip } from '@operations/components/filter/filter.model'
import { FilterChips } from '@operations/components/filter/FilterChips'
import { useVesselVisitBreadcrumbs } from '@operations/hooks/useVesselVisitBreadcrumbs'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'
import { HatchCoverToolbar } from './components/molecules/HatchCoverToolbar'
import { HatchCoverMovesTable } from './components/organisms/HatchCoverMovesTable'
import { HatchcoverFilterUIStore } from './store/hatchcover-filter.ui-store'

export const HatchCoverMovesPage = observer(() => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const id = useParams<{ id: string }>().id
  const navigate = useNavigate()
  const visitId = Number(id)
  const { hatchCoverMoveStore, carrierVisitStore, appStore } = useOperationsStore()
  const title = t('hatchCoverMoves', 'Hatch cover moves')

  useEffect(() => {
    async function fetchVisit() {
      carrierVisitStore.loadOne(visitId)
    }
    if (!carrierVisitStore.items.find(v => v.id === visitId)) fetchVisit()
  }, [carrierVisitStore, visitId])

  useEffect(() => {
    const fetchData = async () => {
      await hatchCoverMoveStore.fetchDataByVisitId(visitId)
    }
    fetchData()
  }, [hatchCoverMoveStore, visitId])

  const vesselVisit = carrierVisitStore.items.find(vesselVisit => vesselVisit.id === visitId)

  const { breadcrumbs } = useVesselVisitBreadcrumbs(vesselVisit)

  const { totalOpened, totalClosed } = hatchCoverMoveStore

  const hatchCoverFilterUiStore = useMemo(
    () => new HatchcoverFilterUIStore(hatchCoverMoveStore),
    [hatchCoverMoveStore],
  )

  const handleRemoveFilter = (value: FilterChip) => {
    hatchCoverFilterUiStore.removeFilter(value)
  }

  const handleReset = () => {
    hatchCoverFilterUiStore.setFilter(null)
    hatchCoverFilterUiStore.toggle()
  }

  const handleDelete = async (id: number) => {
    appStore.showAlertForCancellableRequest(
      'success',
      t('hatchCoverMoveDeleted', 'Hatch cover move deleted'),
      async () => await hatchCoverMoveStore.onDelete(id),
    )
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        minHeight: '100%',
      }}
    >
      <Breadcrumbs items={breadcrumbs} />
      <Header
        title={title}
        position='relative'
        rightRenderOption={
          <Button
            variant='contained'
            color='primary'
            data-cy='create-hatch-cover-move-item-btn'
            onClick={() =>
              navigate(
                `${PATHS.visits.vesselVisits.url}/${vesselVisit?.id}/${PATHS.visits.vesselVisits.hatchCovers.url}/0/upsert`,
              )
            }
          >
            {t('create', 'Create')}
          </Button>
        }
      />
      <Container data-cy='hatch-cover-move-table'>
        <HatchCoverToolbar
          opened={totalOpened}
          closed={totalClosed}
          uiStore={hatchCoverFilterUiStore}
        />
        <FilterChips
          filters={hatchCoverFilterUiStore.filterChips}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleReset}
        />
        <HatchCoverMovesTable
          data={hatchCoverFilterUiStore.filteredItems}
          vesselVisitId={visitId}
          onDelete={handleDelete}
        />
      </Container>
    </Box>
  )
})
